import React, { useCallback, useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Form, Modal } from "react-bootstrap";
import type { AnswerFormat, RagReasoningPace } from "../../../utils/answerFormat";
import {
  ANSWER_FORMAT,
  LEGAL_CUSTOM_INSTRUCTIONS_STORAGE_KEY,
  LEGAL_RAG_REASONING_PACE_STORAGE_KEY,
  RAG_REASONING_PACE_ORDER,
} from "../../../utils/answerFormat";
import styles from "./AnswerFormatModal.module.scss";

export interface AnswerFormatModalProps {
  show: boolean;
  questionPreview: string;
  showCarouselToggle?: boolean;
  displayCarousel?: boolean;
  onDisplayCarouselChange?: (checked: boolean) => void;
  onHide: () => void;
  /** In CUSTOM mode, the second argument is the instruction text (may be empty). Third = RAG concision (panneaux). */
  onConfirm: (
    format: AnswerFormat,
    customInstructions?: string,
    ragReasoningPace?: RagReasoningPace
  ) => void;
  /** Quand true : badge « WIP dev » sur l’option Table (pipeline multi-doc déjà le défaut côté API). */
  devMode?: boolean;
}

const FORMAT_OPTIONS: {
  value: AnswerFormat;
  title: string;
  description: string;
}[] = [
  {
    value: ANSWER_FORMAT.AUTO,
    title: "Auto",
    description:
      "Best for most questions. Georges chooses the layout that fits your question (text or table).",
  },
  {
    value: ANSWER_FORMAT.TABLE,
    title: "Table",
    description:
      "Ideal when you want to compare the same kind of detail across several documents.",
  },
  {
    value: ANSWER_FORMAT.SYNTHESIS,
    title: "Synthesis",
    description:
      "A clear written synthesis with sections and bullet points.",
  },
  {
    value: ANSWER_FORMAT.CUSTOM,
    title: "Custom",
    description:
      "You write short instructions so Georges follows your preferred structure.",
  },
];

const FORMAT_VALUE_ORDER = FORMAT_OPTIONS.map((o) => o.value);

const RAG_PACE_SLIDER_LABELS: Record<RagReasoningPace, { title: string; hint: string }> = {
  slow: {
    title: "Thorough",
    hint: "Full reasoning depth; richer panel text (default).",
  },
  medium: {
    title: "Balanced",
    hint: "Shorter answers — tight bullets, less prose, same citation rules.",
  },
  fast: {
    title: "Fast",
    hint:
      "Barely any added prose — telegraphic, cited facts only; readability is optional.",
  },
};

/**
 * Answer-format choice before send (Exhaustive search: legal, oreima, market, mydeal).
 */
const AnswerFormatModal: React.FC<AnswerFormatModalProps> = ({
  show,
  questionPreview,
  showCarouselToggle = false,
  displayCarousel = false,
  onDisplayCarouselChange,
  onHide,
  onConfirm,
  devMode = false,
}) => {
  const [format, setFormat] = useState<AnswerFormat>(ANSWER_FORMAT.AUTO);
  const [customInstructions, setCustomInstructions] = useState("");
  const [saveNotice, setSaveNotice] = useState(false);
  const groupId = useId();
  const headingId = useId();
  const questionRegionId = useId();
  const carouselId = useId();
  const reasoningPaceSliderId = useId();
  const [reasoningPaceIndex, setReasoningPaceIndex] = useState(0);

  const focusFormatRadio = useCallback(
    (value: AnswerFormat) => {
      const el = document.getElementById(
        `${groupId}-${value}`
      ) as HTMLInputElement | null;
      el?.focus();
    },
    [groupId]
  );

  useEffect(() => {
    if (!show) return;
    setFormat(ANSWER_FORMAT.AUTO);
    try {
      const stored = localStorage.getItem(LEGAL_CUSTOM_INSTRUCTIONS_STORAGE_KEY);
      setCustomInstructions(stored ?? "");
    } catch {
      setCustomInstructions("");
    }
    try {
      const raw = localStorage.getItem(LEGAL_RAG_REASONING_PACE_STORAGE_KEY);
      const idx = raw ? RAG_REASONING_PACE_ORDER.indexOf(raw as RagReasoningPace) : -1;
      setReasoningPaceIndex(idx >= 0 ? idx : 0);
    } catch {
      setReasoningPaceIndex(0);
    }
    setSaveNotice(false);
  }, [show]);

  /** Recentrer le focus sur une option après ouverture (le Modal met souvent le focus sur le bouton fermer). */
  const focusFirstFormatOption = useCallback(() => {
    window.requestAnimationFrame(() => {
      focusFormatRadio(ANSWER_FORMAT.AUTO);
      window.setTimeout(() => focusFormatRadio(ANSWER_FORMAT.AUTO), 50);
    });
  }, [focusFormatRadio]);

  /**
   * Flèches haut/bas : changent le format depuis tout le formulaire (focus sur la croix,
   * les boutons ou les cartes), sauf dans la zone d’instructions personnalisées (textarea).
   */
  const handleFormArrowKeys = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const raw = e.target;
      if (raw instanceof HTMLTextAreaElement) return;

      e.preventDefault();
      e.stopPropagation();

      const idx = FORMAT_VALUE_ORDER.indexOf(format);
      const safeIdx = idx === -1 ? 0 : idx;
      const delta = e.key === "ArrowDown" ? 1 : -1;
      const nextIdx =
        (safeIdx + delta + FORMAT_VALUE_ORDER.length) % FORMAT_VALUE_ORDER.length;
      const nextVal = FORMAT_VALUE_ORDER[nextIdx]!;
      setFormat(nextVal);
      window.requestAnimationFrame(() => focusFormatRadio(nextVal));
    },
    [format, focusFormatRadio]
  );

  const handleSaveCustomInstructions = () => {
    try {
      localStorage.setItem(
        LEGAL_CUSTOM_INSTRUCTIONS_STORAGE_KEY,
        customInstructions.trim()
      );
      setSaveNotice(true);
      window.setTimeout(() => setSaveNotice(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pace = RAG_REASONING_PACE_ORDER[reasoningPaceIndex] ?? RAG_REASONING_PACE_ORDER[0];
    try {
      localStorage.setItem(LEGAL_RAG_REASONING_PACE_STORAGE_KEY, pace);
    } catch {
      /* ignore */
    }
    if (format === ANSWER_FORMAT.CUSTOM) {
      onConfirm(format, customInstructions.trim(), pace);
      return;
    }
    onConfirm(format, undefined, pace);
  };

  const currentPace = RAG_REASONING_PACE_ORDER[reasoningPaceIndex] ?? RAG_REASONING_PACE_ORDER[0];
  const paceCopy = RAG_PACE_SLIDER_LABELS[currentPace];

  const modal = (
    <Modal
      show={show}
      onHide={onHide}
      onEntered={focusFirstFormatOption}
      centered
      animation
      className={`answer-format-modal ${styles.modalRoot}`}
      backdropClassName="answer-format-modal-backdrop"
      aria-labelledby={headingId}
    >
      <form onSubmit={handleSubmit} onKeyDown={handleFormArrowKeys}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <div>
            <Modal.Title id={headingId} as="h2" className={styles.modalTitle}>
              Confirm send
            </Modal.Title>
            <p className={styles.modalSubtitle}>
              Check your question, then pick how you want the reply laid out before
              Georges answers.
            </p>
          </div>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <section
            className={styles.questionSection}
            aria-labelledby={questionRegionId}
          >
            <div
              id={questionRegionId}
              className={styles.questionSectionLabel}
            >
              <i
                className={`bi bi-chat-quote ${styles.questionIcon}`}
                aria-hidden
              />
              Your question
            </div>
            <div className={styles.questionPreview} role="status">
              {questionPreview.trim() ? (
                questionPreview
              ) : (
                <span className={styles.questionEmpty}>
                  (No text — enter a question in the field below, then close this
                  dialog.)
                </span>
              )}
            </div>
          </section>

          <div className={styles.formatSectionLabel}>Answer format</div>
          <p className={styles.formatKeyboardHint}>
            <kbd className={styles.kbd}>↑</kbd>
            <kbd className={styles.kbd}>↓</kbd>
            to move between options
          </p>
          <div
            className={styles.optionsGrid}
            role="radiogroup"
            aria-label="Answer format"
          >
            {FORMAT_OPTIONS.map((opt) => {
              const inputId = `${groupId}-${opt.value}`;
              const selected = format === opt.value;
              const showTableDevBadge =
                devMode && opt.value === ANSWER_FORMAT.TABLE;
              return (
                <label
                  key={opt.value}
                  htmlFor={inputId}
                  className={`${styles.optionCard} ${
                    selected ? styles.optionCardSelected : ""
                  } ${showTableDevBadge ? styles.optionCardWip : ""}`}
                >
                  <input
                    id={inputId}
                    type="radio"
                    name={groupId}
                    value={opt.value}
                    className={styles.optionInput}
                    checked={selected}
                    onChange={() => setFormat(opt.value)}
                    aria-label={`${opt.title}. ${opt.description}`}
                  />
                  <span className={styles.optionTitle}>
                    {opt.title}
                    {showTableDevBadge && (
                      <span className={styles.optionDevBadge}>
                        WIP dev
                      </span>
                    )}
                  </span>
                  <span className={styles.optionDescription}>
                    {showTableDevBadge
                      ? "Force un rendu tableau explicite (comportement multi-doc structuré côté serveur)."
                      : opt.description}
                  </span>
                </label>
              );
            })}
          </div>

          {format === ANSWER_FORMAT.CUSTOM && (
            <div className={styles.customRow}>
              <div className={styles.customRowLabel}>
                Instructions for the answer
              </div>
              <Form.Control
                as="textarea"
                rows={4}
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder="e.g. two sections (facts, then analysis); prefer bullet lists…"
                className={styles.customTextarea}
              />
              <div className={styles.customRowActions}>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleSaveCustomInstructions}
                >
                  Save
                </Button>
                {saveNotice ? (
                  <span className={styles.saveNotice}>Saved</span>
                ) : null}
              </div>
            </div>
          )}

          <div className={styles.reasoningPaceSection}>
            <div className={styles.reasoningPaceLabel}>
              <span className={styles.reasoningPaceTitle}>Reasoning pace</span>
              <span className={styles.reasoningPaceValue} aria-live="polite">
                {paceCopy.title}
              </span>
            </div>
            <p className={styles.reasoningPaceHint}>{paceCopy.hint}</p>
            <Form.Range
              id={reasoningPaceSliderId}
              className={styles.reasoningPaceSlider}
              min={0}
              max={RAG_REASONING_PACE_ORDER.length - 1}
              step={1}
              value={reasoningPaceIndex}
              onChange={(e) =>
                setReasoningPaceIndex(Number.parseInt(e.target.value, 10) || 0)
              }
              aria-valuetext={paceCopy.title}
              aria-label="Reasoning pace: thorough, balanced, or fast"
            />
            <div className={styles.reasoningPaceTicks}>
              <span>Thorough</span>
              <span>Balanced</span>
              <span>Fast</span>
            </div>
          </div>

          {showCarouselToggle && (
            <div className={`${styles.carouselRow} ${styles.carouselRowBottom}`}>
              <input
                id={carouselId}
                type="checkbox"
                className={styles.carouselCheckbox}
                checked={displayCarousel}
                onChange={(e) => onDisplayCarouselChange?.(e.target.checked)}
              />
              <div className={styles.carouselCopy}>
                <label htmlFor={carouselId} className={styles.carouselLabel}>
                  Show intermediate steps
                </label>
                <p className={styles.carouselHint}>
                  Optional: see Georges&apos; short updates and progress while he works
                  through your documents, before the final answer.
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button variant="outline-secondary" type="button" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Send question
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );

  if (typeof document === "undefined") {
    return null;
  }
  return createPortal(modal, document.body);
};

export default AnswerFormatModal;
